// RecurrenceConfigForm component
// Reusable form component for configuring recurrence rules
// CHANGE: Created to extract common recurrence configuration logic from modals
// CHANGE: Encapsulates state management, validation, and UI for recurrence configuration
// CHANGE: Added presets, end date options, and real-time preview
// CHANGE: Optimized UX - Moved preview to bottom, simplified end recurrence cards, removed next occurrences calculation

import { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import type { RecurrenceRule } from '../../lib/supabase';
import { DatePicker } from '../ui/DatePicker';
import { formatDate } from '../../lib/dateUtils';
import { CustomSelect } from '../ui/CustomSelect';

export type RecurrencePreset = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
export type EndMode = 'never' | 'date' | 'occurrences';

export interface RecurrenceConfig {
  preset: RecurrencePreset;
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  daysOfWeek: number[];
  monthlyMode: 'day' | 'week';
  dayOfMonth: number;
  weekOfMonth: number;
  selectedDayForWeek: number;
  interval: number;
  monthOfYear: number;
  yearlyMode: 'day' | 'week';
  endMode: EndMode;
  endDate: string | null;
  endOccurrences: number;
}

interface RecurrenceConfigFormProps {
  initialRule?: RecurrenceRule | null;
  disabled?: boolean;
  showPreview?: boolean;
  onChange?: (config: RecurrenceConfig) => void;
  errorMessage?: string;
}

export function RecurrenceConfigForm({
  initialRule,
  disabled = false,
  showPreview = true,
  onChange,
  errorMessage
}: RecurrenceConfigFormProps) {
  // Determine initial preset from existing rule
  const determineInitialPreset = (): RecurrencePreset => {
    if (!initialRule) return 'daily';
    if (initialRule.interval !== 1) return 'custom';

    switch (initialRule.frequency) {
      case 'daily':
        return 'daily';
      case 'weekly':
        return initialRule.days_of_week.length > 0 ? 'custom' : 'weekly';
      case 'monthly':
      case 'yearly':
        return 'custom';
      default:
        return 'daily';
    }
  };

  // State
  const [preset, setPreset] = useState<RecurrencePreset>(determineInitialPreset());
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>(
    initialRule?.frequency || 'daily'
  );
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(initialRule?.days_of_week || []);
  const [monthlyMode, setMonthlyMode] = useState<'day' | 'week'>(
    initialRule?.day_of_month ? 'day' : 'week'
  );
  const [dayOfMonth, setDayOfMonth] = useState<number>(initialRule?.day_of_month || 1);
  const [weekOfMonth, setWeekOfMonth] = useState<number>(initialRule?.week_of_month || 1);
  const [selectedDayForWeek, setSelectedDayForWeek] = useState<number>(
    initialRule?.days_of_week?.[0] || 1
  );
  const [interval, setInterval] = useState<number>(initialRule?.interval || 1);
  const [monthOfYear, setMonthOfYear] = useState<number>(initialRule?.month_of_year || 1);
  const [yearlyMode, setYearlyMode] = useState<'day' | 'week'>(
    initialRule?.frequency === 'yearly' && initialRule.day_of_month ? 'day' : 'week'
  );
  const [endMode, setEndMode] = useState<EndMode>('never');
  const [endDate, setEndDate] = useState<string | null>(null);
  const [endOccurrences, setEndOccurrences] = useState<number>(10);

  // Constants
  const dayNames = [
    { value: 1, short: 'L', full: 'Lundi' },
    { value: 2, short: 'M', full: 'Mardi' },
    { value: 3, short: 'M', full: 'Mercredi' },
    { value: 4, short: 'J', full: 'Jeudi' },
    { value: 5, short: 'V', full: 'Vendredi' },
    { value: 6, short: 'S', full: 'Samedi' },
    { value: 7, short: 'D', full: 'Dimanche' }
  ];

  const weekNames = [
    { value: 1, label: 'Premier' },
    { value: 2, label: 'Deuxième' },
    { value: 3, label: 'Troisième' },
    { value: 4, label: 'Quatrième' },
    { value: 5, label: 'Dernier' }
  ];

  const monthNames = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
  ];

  // Handle preset change
  const handlePresetChange = (newPreset: RecurrencePreset) => {
    setPreset(newPreset);

    switch (newPreset) {
      case 'daily':
        setFrequency('daily');
        setInterval(1);
        setDaysOfWeek([]);
        break;
      case 'weekly':
        setFrequency('weekly');
        setInterval(1);
        setDaysOfWeek([]); // No specific days for simple weekly preset
        break;
      case 'monthly':
        setFrequency('monthly');
        setInterval(1);
        setMonthlyMode('day');
        setDayOfMonth(1);
        break;
      case 'yearly':
        setFrequency('yearly');
        setInterval(1);
        setYearlyMode('day');
        setMonthOfYear(1);
        setDayOfMonth(1);
        break;
      case 'custom':
        // Keep current values
        break;
    }
  };

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      onChange({
        preset,
        frequency,
        daysOfWeek,
        monthlyMode,
        dayOfMonth,
        weekOfMonth,
        selectedDayForWeek,
        interval,
        monthOfYear,
        yearlyMode,
        endMode,
        endDate,
        endOccurrences
      });
    }
  }, [preset, frequency, daysOfWeek, monthlyMode, dayOfMonth, weekOfMonth, selectedDayForWeek,
      interval, monthOfYear, yearlyMode, endMode, endDate, endOccurrences, onChange]);

  const toggleDayOfWeek = (day: number) => {
    if (daysOfWeek.includes(day)) {
      setDaysOfWeek(daysOfWeek.filter(d => d !== day));
    } else {
      setDaysOfWeek([...daysOfWeek, day].sort((a, b) => a - b));
    }
  };

  // Generate preview text
  const generatePreview = (): string => {
    switch (frequency) {
      case 'daily':
        if (interval === 1) {
          return 'Tous les jours';
        }
        return `Tous les ${interval} jours`;

      case 'weekly':
        if (daysOfWeek.length === 0) {
          // Simple weekly preset - no specific days, just 7-day interval
          if (interval === 1) {
            return 'Toutes les semaines';
          }
          return `Toutes les ${interval} semaines`;
        }
        // Custom weekly with specific days
        const selectedDays = daysOfWeek
          .map(day => dayNames.find(d => d.value === day)?.full)
          .join(', ');
        if (interval === 1) {
          return `Tous les ${selectedDays}`;
        }
        return `Toutes les ${interval} semaines le ${selectedDays}`;

      case 'monthly':
        if (interval === 1) {
          return 'Tous les mois';
        }
        return `Tous les ${interval} mois`;

      case 'yearly':
        if (interval === 1) {
          return 'Tous les ans';
        }
        return `Tous les ${interval} ans`;

      default:
        return '';
    }
  };


  const today = formatDate(new Date());

  return (
    <div className="space-y-6">
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {errorMessage}
        </div>
      )}

      {/* Preset selector */}
      <div>
        <label htmlFor="recurrence-preset" className="block text-sm font-medium text-gray-700 mb-2">
          Type de récurrence <span className="text-red-500">*</span>
        </label>
        <CustomSelect
          value={preset}
          onChange={(value) => handlePresetChange(value as RecurrencePreset)}
          options={[
            { value: 'daily', label: 'Tous les jours' },
            { value: 'weekly', label: 'Toutes les semaines' },
            { value: 'monthly', label: 'Tous les mois' },
            { value: 'yearly', label: 'Tous les ans' },
            { value: 'custom', label: 'Personnaliser...' }
          ]}
          disabled={disabled}
          placeholder="Sélectionner un type"
        />
      </div>


      {/* Custom configuration */}
      {preset === 'custom' && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
          <div className="text-sm font-semibold text-gray-900 mb-3">
            Configuration personnalisée
          </div>

          {/* Simplified recurrence line - Google Agenda style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Récurrence <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-700">Répéter tou(te)s les</span>
              <input
                type="number"
                min="1"
                max="365"
                value={interval}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setInterval(1);
                  } else {
                    const numValue = parseInt(value);
                    if (!isNaN(numValue)) {
                      setInterval(Math.max(1, Math.min(365, numValue)));
                    }
                  }
                }}
                disabled={disabled}
                className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-white text-center"
              />
              <CustomSelect
                value={frequency}
                onChange={(value) => setFrequency(value as 'daily' | 'weekly' | 'monthly' | 'yearly')}
                options={[
                  { value: 'daily', label: interval === 1 ? 'jour' : 'jours' },
                  { value: 'weekly', label: interval === 1 ? 'semaine' : 'semaines' },
                  { value: 'monthly', label: 'mois' },
                  { value: 'yearly', label: interval === 1 ? 'an' : 'ans' }
                ]}
                disabled={disabled}
                placeholder="Unité"
              />
            </div>
          </div>

          {/* Weekly day selector - shown only when frequency is weekly */}
          {frequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Répéter le <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                {dayNames.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDayOfWeek(day.value)}
                    disabled={disabled}
                    className={`flex-1 py-3 px-2 rounded-lg border-2 font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      daysOfWeek.includes(day.value)
                        ? 'border-primary-500 bg-primary-500 text-white'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300 bg-white'
                    }`}
                    title={day.full}
                  >
                    {day.short}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Sélectionnez un ou plusieurs jours
              </p>
            </div>
          )}
        </div>
      )}

      {/* End of recurrence section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Fin de récurrence
        </label>
        <div className="space-y-3">
          {/* Never ends */}
          <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="radio"
              name="end-mode"
              value="never"
              checked={endMode === 'never'}
              onChange={() => setEndMode('never')}
              disabled={disabled}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-sm text-gray-900">Ne se termine jamais</div>
            </div>
          </label>

          {/* End date */}
          <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="radio"
              name="end-mode"
              value="date"
              checked={endMode === 'date'}
              onChange={() => setEndMode('date')}
              disabled={disabled}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-sm text-gray-900">Se termine le...</div>
              {endMode === 'date' && (
                <div className="mt-2">
                  <DatePicker
                    value={endDate}
                    onChange={setEndDate}
                    minDate={today}
                    disabled={disabled}
                    placeholder="Sélectionnez une date"
                  />
                </div>
              )}
            </div>
          </label>

          {/* End after N occurrences */}
          <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="radio"
              name="end-mode"
              value="occurrences"
              checked={endMode === 'occurrences'}
              onChange={() => setEndMode('occurrences')}
              disabled={disabled}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-sm text-gray-900">
                {endMode === 'occurrences' ? `Se termine après ${endOccurrences} fois` : 'Se termine après X fois'}
              </div>
              {endMode === 'occurrences' && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={endOccurrences}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value)) {
                        setEndOccurrences(Math.max(1, Math.min(365, value)));
                      }
                    }}
                    disabled={disabled}
                    className="w-24 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700">
                    {endOccurrences === 1 ? 'occurrence' : 'occurrences'}
                  </span>
                </div>
              )}
            </div>
          </label>
        </div>
      </div>

      {/* Preview Section - At the bottom */}
      {showPreview && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Calendar size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm text-blue-900 mb-1">Aperçu de la récurrence</div>
              <div className="text-sm text-blue-700 font-medium">{generatePreview()}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
